import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from 'components/ui/select';
import { useTestOptions } from 'modules/analytics';
import { Hooks as TeamHooks } from 'modules/teams';
import type { AnalyticsFilters } from '../useAnalyticsFilters';

const ALL = '__all__';

interface Props {
  filters: AnalyticsFilters;
  orgId?: string;
  /** The team filter only means something on the People tab. */
  showTeamFilter?: boolean;
}

/** A secondary 32px control: white, hairline border, the current choice and a chevron. */
const triggerClass =
  'h-8 w-auto max-w-[220px] gap-1.5 bg-card px-3 text-[13px] font-medium text-foreground hover:border-foreground/20';

/** "All tests" (and, on People, "All teams") at the end of the tab line. */
const AnalyticsFilterBar: React.FC<Props> = ({ filters, orgId, showTeamFilter = false }) => {
  const { t } = useTranslation();
  const { data: testOptions } = useTestOptions({ orgId });
  const { teams } = TeamHooks.useList({ enabled: !!orgId && showTeamFilter });

  return (
    <div className="flex items-center gap-2">
      {showTeamFilter && (
        <Select value={filters.teamId ?? ALL} onValueChange={(v) => filters.setTeamId(v === ALL ? undefined : v)}>
          <SelectTrigger className={triggerClass}>
            <SelectValue placeholder={t('analytics_all_teams')} />
          </SelectTrigger>
          <SelectContent align="end">
            <SelectItem value={ALL}>{t('analytics_all_teams')}</SelectItem>
            {(teams ?? []).map((team: { id: string; name: string }) => (
              <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <Select value={filters.testId ?? ALL} onValueChange={(v) => filters.setTestId(v === ALL ? undefined : v)}>
        <SelectTrigger className={triggerClass}>
          <SelectValue placeholder={t('analytics_all_tests')} />
        </SelectTrigger>
        <SelectContent align="end">
          <SelectItem value={ALL}>{t('analytics_all_tests')}</SelectItem>
          {(testOptions ?? []).map((opt) => (
            <SelectItem key={opt.id} value={opt.id}>
              {opt.title}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default AnalyticsFilterBar;
